import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { ToastVariant, useLanguage, useProvider, useToastMessage } from '~/Hooks';
import useActionPolling from '~/Hooks/usePolling/useActionPolling';
import { requestDownloadJobs } from '~/store';
import { requestMarkDownloadAsDone } from '~/store/app';
import {
  selectAllJobs,
  selectFailedJobs,
  selectPendingJobs,
  selectSuccessfulJobs,
} from '~/store/downloads';
export type PrivateLayoutType = ReturnType<typeof usePrivateLayout>;

export const usePrivateLayout = () => {
  const { currentProvider } = useProvider();
  const dispatch = useDispatch();
  const { showToast, showDownloadToast } = useToastMessage();

  const { t } = useLanguage();

  const downloadStatus = bindActionCreators(requestDownloadJobs, dispatch);
  const markDownloadDone = bindActionCreators(requestMarkDownloadAsDone, dispatch);

  const requestedDownloads = useSelector(selectAllJobs);
  const pendingDownloads = useSelector(selectPendingJobs);
  const readyDownloads = useSelector(selectSuccessfulJobs);
  const failedDownloads = useSelector(selectFailedJobs);

  const removeDownloadJob = (jobId: string) => {
    markDownloadDone(jobId);
  };

  const statusAction = (domain: string) => () => downloadStatus(domain);

  const { addPollableAction, removePollableAction, stopPolling } = useActionPolling(1000);

  useEffect(() => {
    if (requestedDownloads.length > 0 && pendingDownloads.length > 0) {
      addPollableAction(REQUEST_ACTIONS.JOB_STATUS, statusAction(currentProvider?.domain ?? ''));
    } else {
      removePollableAction(REQUEST_ACTIONS.JOB_STATUS);
    }

    readyDownloads.forEach((download) => {
      showDownloadToast(
        download.id,
        t('app.download.ready'),
        download.downloadUrl,
        download.id,
        removeDownloadJob,
        ToastVariant.SUCCESS,
      );
    });

    failedDownloads.forEach((download) => {
      showToast(download.id, t('app.default.error'), ToastVariant.ERROR, true, removeDownloadJob);
    });

    return () => {
      removePollableAction(REQUEST_ACTIONS.JOB_STATUS);
    };
  }, [requestedDownloads, currentProvider]);

  useEffect(
    () => () => {
      stopPolling();
    },
    [],
  );
  return {};
};
